import { useEffect, useState, useCallback } from "react";

export const useTelegram = () => {
  const [tgUser, setTgUser] = useState<TelegramWebAppUser | null>(null);
  const [isInTelegram, setIsInTelegram] = useState(false);
  const [colorScheme, setColorScheme] = useState<"light" | "dark">("dark");

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      setIsInTelegram(true);
      setColorScheme(tg.colorScheme || "dark");

      if (tg.initDataUnsafe?.user) {
        setTgUser(tg.initDataUnsafe.user);
      }

      // Apply Telegram theme params as CSS custom properties
      const params = tg.themeParams;
      if (params) {
        const root = document.documentElement;
        const themeMap: Record<string, string> = {
          bg_color: "--tg-theme-bg-color",
          text_color: "--tg-theme-text-color",
          hint_color: "--tg-theme-hint-color",
          link_color: "--tg-theme-link-color",
          button_color: "--tg-theme-button-color",
          button_text_color: "--tg-theme-button-text-color",
          secondary_bg_color: "--tg-theme-secondary-bg-color",
          header_bg_color: "--tg-theme-header-bg-color",
          accent_text_color: "--tg-theme-accent-text-color",
          section_bg_color: "--tg-theme-section-bg-color",
          section_header_text_color: "--tg-theme-section-header-text-color",
          subtitle_text_color: "--tg-theme-subtitle-text-color",
          destructive_text_color: "--tg-theme-destructive-text-color",
        };

        Object.entries(themeMap).forEach(([key, cssVar]) => {
          const value = params[key as keyof typeof params];
          if (value) {
            root.style.setProperty(cssVar, value);
          }
        });

        // Add a class to indicate Telegram theming is active
        root.classList.add("tg-themed");
      }

      // Real-time theme change listener (Feature #18)
      const handleThemeChange = () => {
        setColorScheme(tg.colorScheme || "dark");
      };
      tg.onEvent("themeChanged", handleThemeChange);

      return () => {
        tg.offEvent("themeChanged", handleThemeChange);
      };
    }
  }, []);

  const closeTelegram = useCallback(() => {
    window.Telegram?.WebApp?.close();
  }, []);

  const openExternalLink = useCallback((url: string) => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openLink(url);
    } else {
      window.open(url, "_blank");
    }
  }, []);

  const isHapticSupported = useCallback(() => {
    const webApp = window.Telegram?.WebApp;
    if (!webApp?.HapticFeedback) return false;
    if (typeof webApp.isVersionAtLeast === "function") {
      return webApp.isVersionAtLeast("6.1");
    }
    return true;
  }, []);

  const hapticFeedback = {
    impact: useCallback((style: "light" | "medium" | "heavy" | "rigid" | "soft" = "medium") => {
      if (isHapticSupported()) {
        window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
      }
    }, [isHapticSupported]),
    notification: useCallback((type: "error" | "success" | "warning") => {
      if (isHapticSupported()) {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
      }
    }, [isHapticSupported]),
    selection: useCallback(() => {
      if (isHapticSupported()) {
        window.Telegram?.WebApp?.HapticFeedback?.selectionChanged();
      }
    }, [isHapticSupported]),
  };

  const initData = window.Telegram?.WebApp?.initData || null;

  const showPopup = useCallback((params: {
    title?: string;
    message: string;
    buttons?: Array<{ id?: string; type?: "default" | "ok" | "close" | "cancel" | "destructive"; text: string }>;
  }) => {
    return new Promise<string | null>((resolve) => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup(params, (buttonId) => {
          resolve(buttonId || null);
        });
      } else {
        alert(params.message);
        resolve(null);
      }
    });
  }, []);

  return { tgUser, isInTelegram, colorScheme, closeTelegram, openExternalLink, hapticFeedback, initData, showPopup };
};

